/** @file cubeProjection: the orientation cube seen by the editor camera (visible faces, quads, depth order). */
import { orthoBasis } from '@zilo/pose3d-math/project/orthoBasis';
import type { Vec3 } from '@zilo/pose3d-math/vec3/vec3';
import { CUBE_FACES, type CubeFace, type CubeFaceId } from './cubeFaces';

/** A visible face: its quad and centre in cube units on screen (x right, y down), view depth (grows away), facing. */
export type ProjectedFace = { id: CubeFaceId; face: CubeFace; quad: [number, number][]; centre: [number, number]; depth: number; facing: number };

/** Visible faces far to near (paint in order), and the face whose normal points most at the camera. */
export type CubeProjection = { faces: ProjectedFace[]; front: CubeFaceId };

/** Eye distance from the cube centre in half-edges: a mild perspective, so the cube reads as a solid. */
const EYE = 8;
const SQUARE: readonly [number, number][] = [[-1, -1], [1, -1], [1, 1], [-1, 1]];

const dot = (a: Vec3, b: Vec3): number => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];

/** The four corners of the face with normal `n` (the unit cube spans -1..1), in order around it. */
function corners(n: Vec3): Vec3[] {
  const axis = n.findIndex((c) => c !== 0);
  const u = (axis + 1) % 3;
  const v = (axis + 2) % 3;
  return SQUARE.map(([a, b]) => {
    const p = [0, 0, 0];
    p[axis] = n[axis] ?? 0;
    p[u] = a;
    p[v] = b;
    return [p[0] ?? 0, p[1] ?? 0, p[2] ?? 0];
  });
}

/**
 * Projects the unit cube with the viewport camera's basis (`orthoBasis`, the one the scene uses) plus a perspective
 * divide from an eye `EYE` half-edges back. A face is visible when the eye is on its outer side; faces are sorted by
 * the depth of their centres, farthest first; `front` is the face with the largest `normal . back`.
 */
export function cubeProjection(yawDeg: number, pitchDeg: number): CubeProjection {
  const { right, up, back } = orthoBasis(yawDeg, pitchDeg);
  const project = (p: Vec3): [number, number] => {
    const k = EYE / (EYE - dot(p, back));
    return [dot(p, right) * k, -dot(p, up) * k];
  };
  const faces: ProjectedFace[] = [];
  let front: CubeFace = CUBE_FACES[0] as CubeFace;
  for (const face of CUBE_FACES) {
    const facing = dot(face.normal, back);
    if (facing > dot(front.normal, back)) front = face;
    if (EYE * facing <= 1) continue;
    faces.push({ id: face.id, face, quad: corners(face.normal).map(project), centre: project(face.normal), depth: -dot(face.normal, back), facing });
  }
  faces.sort((a, b) => b.depth - a.depth);
  return { faces, front: front.id };
}
